import React, { RefObject } from 'react';
import { Badge, Button, Table } from 'react-bootstrap';
import dayjs from 'dayjs';
import { OnCallAssignment } from 'services/api/callout';

export const CalendarMonthGrid = ({
  calendarMonths,
  getMonthDays,
  assignmentsForDay,
  assignmentsLoading,
  canWrite,
  openSubstituteForAssignment,
  calendarLoadMoreRef,
  onLoadMoreMonths
}: {
  calendarMonths: dayjs.Dayjs[];
  getMonthDays: (month: dayjs.Dayjs) => dayjs.Dayjs[];
  assignmentsForDay: (day: dayjs.Dayjs) => OnCallAssignment[];
  assignmentsLoading: boolean;
  canWrite: boolean;
  openSubstituteForAssignment: (
    assignmentId: string,
    day?: dayjs.Dayjs
  ) => void;
  calendarLoadMoreRef: RefObject<HTMLDivElement>;
  onLoadMoreMonths: () => void;
}) => {
  const getAssigneeColor = (
    colors: unknown,
    userId: string
  ): string | undefined => {
    if (!colors) return undefined;
    const maybeMap = colors as any;
    if (typeof maybeMap.get === 'function') {
      return maybeMap.get(userId);
    }
    return (colors as Record<string, string>)[userId];
  };

  return (
    <div id="callout-calendar-view" className="p-0">
      {calendarMonths.map(month => {
        const monthDays = getMonthDays(month);
        const rows = Array.from({
          length: Math.ceil(monthDays.length / 7)
        });
        return (
          <div key={month.format('YYYY-MM')} className="border-top">
            <div className="px-3 py-2 bg-body-tertiary fw-semibold">
              {month.format('MMMM YYYY')}
            </div>
            <Table responsive className="mb-0">
              <thead className="bg-body-tertiary text-700">
                <tr>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <th key={d} className="text-center">
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((_w, wIdx) => (
                  <tr key={wIdx}>
                    {monthDays.slice(wIdx * 7, wIdx * 7 + 7).map(day => {
                      const inMonth = day.month() === month.month();
                      const dayAssignments = assignmentsForDay(day);
                      return (
                        <td
                          key={day.format('YYYY-MM-DD')}
                          className="align-top"
                          style={{
                            minWidth: 140,
                            opacity: inMonth ? 1 : 0.35
                          }}
                        >
                          <div className="d-flex justify-content-between">
                            <div className="fw-semibold">{day.date()}</div>
                          </div>
                          <div className="mt-2 d-flex flex-column gap-1">
                            {assignmentsLoading && (
                              <small className="text-600">Loading…</small>
                            )}
                            {!assignmentsLoading &&
                              dayAssignments.map(a => (
                                <div
                                  key={a._id}
                                  className="d-flex flex-wrap align-items-center gap-1"
                                >
                                  {a.assignees.map(u => {
                                    const color = getAssigneeColor(
                                      (a as any).assigneeColors,
                                      u._id
                                    );
                                    return (
                                      <Badge
                                        key={u._id}
                                        bg={color ? '' : 'secondary'}
                                        pill
                                        style={
                                          color
                                            ? {
                                                backgroundColor: color,
                                                border:
                                                  '1px solid rgba(0, 0, 0, 0.15)'
                                              }
                                            : undefined
                                        }
                                      >
                                        {u.firstName} {u.lastName}
                                      </Badge>
                                    );
                                  })}
                                  {canWrite && (
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="p-0 ms-1 callout-calendar-print-hide"
                                      onClick={() =>
                                        openSubstituteForAssignment(a._id, day)
                                      }
                                    >
                                      Sub
                                    </Button>
                                  )}
                                </div>
                              ))}
                            {!assignmentsLoading && !dayAssignments.length && (
                              <small className="text-600">—</small>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        );
      })}
      <div className="p-3 callout-calendar-print-hide">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onLoadMoreMonths}
        >
          Load more months
        </Button>
        <div ref={calendarLoadMoreRef} />
      </div>
    </div>
  );
};
